import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Package, Car, Mail, Users, LogOut, Plus, Pencil, Trash2,
  Eye, Check, X, Loader2, Menu, ChevronDown, BarChart3, Clock, Settings, Image, FileText, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { MediaManager, ImagePickerButton } from '../components/MediaManager';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

// Tab components
import DashboardTab from './admin/DashboardTab';
import PackagesTab from './admin/PackagesTab';
import FleetTab from './admin/FleetTab';
import TeamTab from './admin/TeamTab';
import EnquiriesTab from './admin/EnquiriesTab';
import SiteContentTab from './admin/SiteContentTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Route Editor sub-component
const RouteStopEditor = ({ routes, onChange }) => {
  const route = (routes && routes[0]) || { name: '', type: 'road', stops: [] };
  const stops = route.stops || [];
  const updateRoute = (field, value) => onChange([{ ...route, [field]: value }]);
  const addStop = () => updateRoute('stops', [...stops, { name: '', coordinates: [0, 0], day: stops.length > 0 ? stops[stops.length - 1].day : 1, description: '' }]);
  const removeStop = (idx) => updateRoute('stops', stops.filter((_, i) => i !== idx));
  const updateStop = (idx, field, value) => {
    const updated = stops.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    updateRoute('stops', updated);
  };

  return (
    <div className="border border-s-border rounded-lg p-4 space-y-3" data-testid="route-editor">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-s-text">Route & Map Stops</Label>
        <button type="button" onClick={addStop} className="text-xs bg-s-primary hover:bg-s-primary-h text-white px-3 py-1 rounded-md" data-testid="add-stop-btn">+ Add Stop</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-s-text2">Route Name</Label>
          <Input value={route.name || ''} onChange={(e) => updateRoute('name', e.target.value)} className="bg-s-card border-s-border text-sm" placeholder="e.g., Bali Discovery Route" />
        </div>
        <div>
          <Label className="text-xs text-s-text2">Route Type</Label>
          <Select value={route.type || 'road'} onValueChange={(v) => updateRoute('type', v)}>
            <SelectTrigger className="bg-s-card border-s-border text-sm"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-s-card border-s-border">
              <SelectItem value="road">Road</SelectItem>
              <SelectItem value="flight">Flight</SelectItem>
              <SelectItem value="seaplane">Seaplane</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {stops.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {stops.map((stop, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_80px_80px_1fr_32px] gap-2 items-end bg-s-card/50 p-2 rounded-md" data-testid={`route-stop-${idx}`}>
              <div><Label className="text-[10px] text-s-muted">Place Name</Label><Input value={stop.name} onChange={(e) => updateStop(idx, 'name', e.target.value)} className="bg-s-bg border-s-border text-xs h-8" placeholder="Stop name" /></div>
              <div><Label className="text-[10px] text-s-muted">Lat</Label><Input type="number" step="any" value={stop.coordinates[0]} onChange={(e) => updateStop(idx, 'coordinates', [parseFloat(e.target.value) || 0, stop.coordinates[1]])} className="bg-s-bg border-s-border text-xs h-8" /></div>
              <div><Label className="text-[10px] text-s-muted">Lng</Label><Input type="number" step="any" value={stop.coordinates[1]} onChange={(e) => updateStop(idx, 'coordinates', [stop.coordinates[0], parseFloat(e.target.value) || 0])} className="bg-s-bg border-s-border text-xs h-8" /></div>
              <div className="grid grid-cols-[50px_1fr] gap-1">
                <div><Label className="text-[10px] text-s-muted">Day</Label><Input type="number" value={stop.day} onChange={(e) => updateStop(idx, 'day', parseInt(e.target.value) || 1)} className="bg-s-bg border-s-border text-xs h-8" /></div>
                <div><Label className="text-[10px] text-s-muted">Description</Label><Input value={stop.description} onChange={(e) => updateStop(idx, 'description', e.target.value)} className="bg-s-bg border-s-border text-xs h-8" placeholder="Brief note" /></div>
              </div>
              <button type="button" onClick={() => removeStop(idx)} className="text-s-primary hover:text-s-primary h-8 flex items-center justify-center" data-testid={`remove-stop-${idx}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {stops.length === 0 && <p className="text-s-muted text-xs text-center py-3">No stops added yet. Click "+ Add Stop" to define the route.</p>}
    </div>
  );
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [dashboardStats, setDashboardStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [legalContent, setLegalContent] = useState({ privacy_policy: '', terms_conditions: '', refund_policy: '' });
  const [savingContent, setSavingContent] = useState(false);
  const [savingLegal, setSavingLegal] = useState(false);
  const { refreshSettings } = useSiteSettings();

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const savedToken = localStorage.getItem('adminToken');
    const savedUser = localStorage.getItem('adminUser');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsLoggedIn(true);
      
      // Fetch dashboard stats immediately with saved token
      fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${savedToken}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setDashboardStats(data); })
        .catch(err => console.error('Dashboard fetch on load:', err));
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, token]);

  useEffect(() => {
    if (isLoggedIn && token) {
      if (activeTab === 'dashboard') fetchDashboardStats();
      if (activeTab === 'packages') fetchPackages();
      if (activeTab === 'fleet') fetchFleet();
      if (activeTab === 'enquiries') fetchEnquiries();
      if (activeTab === 'team') fetchTeamMembers();
      if (activeTab === 'content') fetchSiteContent();
      if (activeTab === 'legal') fetchLegalContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoggedIn, token]);

  const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });

  // Handle 401 errors by logging out (token expired or invalid)
  const handleAuthError = (res) => {
    if (res.status === 401) {
      alert('Session expired. Please log in again.');
      handleLogout();
      return true;
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      if (!res.ok) throw new Error('Invalid credentials');
      const data = await res.json();
      setToken(data.access_token);
      setUser(data.user);
      setIsLoggedIn(true);
      localStorage.setItem('adminToken', data.access_token);
      localStorage.setItem('adminUser', JSON.stringify(data.user));
      
      // Immediately fetch dashboard stats after login
      try {
        const dashRes = await fetch(`${API_URL}/api/admin/dashboard`, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${data.access_token}` }
        });
        if (dashRes.ok) {
          setDashboardStats(await dashRes.json());
        }
      } catch (dashErr) {
        console.error('Dashboard fetch after login:', dashErr);
      }
    } catch (err) {
      setLoginError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false); setToken(null); setUser(null);
    localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser');
  };

  const fetchDashboardStats = async () => { 
    try { 
      const res = await fetch(`${API_URL}/api/admin/dashboard`, { headers: authHeaders() }); 
      if (handleAuthError(res)) return;
      if (res.ok) {
        setDashboardStats(await res.json()); 
      } else {
        console.error('Dashboard fetch failed:', res.status);
      }
    } catch (err) { 
      console.error('Dashboard fetch error:', err); 
    } 
  };
  const fetchPackages = async () => { try { const res = await fetch(`${API_URL}/api/packages`); if (res.ok) setPackages(await res.json()); } catch (err) { console.error(err); } };
  const fetchFleet = async () => { try { const res = await fetch(`${API_URL}/api/fleet`); if (res.ok) setFleet(await res.json()); } catch (err) { console.error(err); } };
  const fetchEnquiries = async () => { try { const res = await fetch(`${API_URL}/api/admin/enquiries`, { headers: authHeaders() }); if (handleAuthError(res)) return; if (res.ok) setEnquiries(await res.json()); } catch (err) { console.error(err); } };
  const fetchTeamMembers = async () => { try { const res = await fetch(`${API_URL}/api/team`); if (res.ok) setTeamMembers(await res.json()); } catch (err) { console.error(err); } };
  const fetchSiteContent = async () => { try { const res = await fetch(`${API_URL}/api/settings`); if (res.ok) setSiteContent(await res.json()); } catch (err) { console.error(err); } };
  const fetchLegalContent = async () => { 
    try { 
      const res = await fetch(`${API_URL}/api/settings`); 
      if (res.ok) {
        const data = await res.json();
        setLegalContent(data.legal || { privacy_policy: '', terms_conditions: '', refund_policy: '' });
      }
    } catch (err) { console.error(err); } 
  };

  const saveLegalContent = async () => {
    setSavingLegal(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ legal: legalContent })
      });
      if (res.ok) { refreshSettings(); alert('Legal pages saved successfully!'); }
    } catch (err) { console.error(err); alert('Failed to save legal pages'); } finally { setSavingLegal(false); }
  };

  const saveSiteContent = async (section, data) => {
    setSavingContent(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ [section]: data })
      });
      if (res.ok) { setSiteContent(await res.json()); refreshSettings(); alert(`${section} saved successfully!`); }
    } catch (err) { console.error(err); } finally { setSavingContent(false); }
  };

  const processFormData = (data) => {
    const processed = { ...data };
    // Convert comma-separated input fields to arrays
    if (processed.citiesInput !== undefined) {
      processed.cities = processed.citiesInput.split(',').map(s => s.trim()).filter(s => s);
      delete processed.citiesInput;
    }
    if (processed.highlightsInput !== undefined) {
      processed.highlights = processed.highlightsInput.split(',').map(s => s.trim()).filter(s => s);
      delete processed.highlightsInput;
    }
    if (processed.inclusionsInput !== undefined) {
      processed.inclusions = processed.inclusionsInput.split(',').map(s => s.trim()).filter(s => s);
      delete processed.inclusionsInput;
    }
    // Auto-calculate duration from itinerary
    const itineraryDays = processed.itinerary?.length || 1;
    processed.duration_days = itineraryDays;
    processed.duration_nights = Math.max(0, itineraryDays - 1);
    return processed;
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const endpoint = modalType === 'package' ? 'packages' : modalType === 'team' ? 'team' : 'fleet';
      const dataToSend = modalType === 'package' ? processFormData(formData) : formData;
      const res = await fetch(`${API_URL}/api/admin/${endpoint}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(dataToSend) });
      if (res.ok) {
        setShowModal(false);
        if (modalType === 'package') fetchPackages();
        if (modalType === 'fleet') fetchFleet();
        if (modalType === 'team') fetchTeamMembers();
        fetchDashboardStats();
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const endpoint = modalType === 'package' ? 'packages' : modalType === 'team' ? 'team' : 'fleet';
      const dataToSend = modalType === 'package' ? processFormData(formData) : formData;
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${editItem.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(dataToSend) });
      if (res.ok) {
        setShowModal(false); setEditItem(null);
        if (modalType === 'package') fetchPackages();
        if (modalType === 'fleet') fetchFleet();
        if (modalType === 'team') fetchTeamMembers();
      }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const endpoint = type === 'package' ? 'packages' : type === 'fleet' ? 'fleet' : type === 'team' ? 'team' : 'enquiries';
      const res = await fetch(`${API_URL}/api/admin/${endpoint}/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        if (type === 'package') fetchPackages();
        if (type === 'fleet') fetchFleet();
        if (type === 'enquiry') fetchEnquiries();
        if (type === 'team') fetchTeamMembers();
        fetchDashboardStats();
      }
    } catch (err) { console.error(err); }
  };

  const handleEnquiryStatus = async (id, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/enquiries/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status }) });
      if (res.ok) { fetchEnquiries(); fetchDashboardStats(); }
    } catch (err) { console.error(err); }
  };

  const openCreateModal = (type) => { setModalType(type); setEditItem(null); setFormData(getDefaultFormData(type)); setShowModal(true); };
  const openEditModal = (type, item) => { setModalType(type); setEditItem(item); setFormData(item); setShowModal(true); };

  const getDefaultFormData = (type) => {
    switch (type) {
      case 'package': return { 
        title: '', 
        destination: '', 
        region: ['south'],
        theme: ['cultural'],
        state: '',
        image: '', 
        images: [],
        duration_nights: 3,
        duration_days: 4,
        cities: [],
        brief: '',
        highlights: [], 
        inclusions: [], 
        exclusions: [],
        itinerary: [],
        faqs: [],
        best_time_to_visit: '',
        interesting_facts: '',
        guide_available: true,
        languages: ['English'],
        is_active: true,
        is_featured: false
      };
      case 'fleet': return { name: '', type: '', image: '', images: [], capacity: '', features: [], available: true };
      case 'team': return { name: '', role: '', image: '', bio: '', years_of_experience: 0, specialization: '', order: 0 };
      default: return {};
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-s-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-s-bg2 border-s-border">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_12ccf344-159c-4ed2-aa19-979c94946606/artifacts/vioo559c_Sentora%20Logo_Primary%20Symbol.png" alt="Sentora" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl text-s-text">Sentora Holidays Admin</CardTitle>
            <p className="text-s-text2">Sign in to manage your website</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
              {loginError && <div className="bg-s-primary/20 border border-red-500/30 text-s-primary px-4 py-3 rounded-lg text-sm">{loginError}</div>}
              <div><Label className="text-s-text">Email</Label><Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="tours@sentoraholidays.com" className="mt-1 bg-s-card border-s-border text-s-text" required data-testid="login-email-input" /></div>
              <div><Label className="text-s-text">Password</Label><Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="********" className="mt-1 bg-s-card border-s-border text-s-text" required data-testid="login-password-input" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-s-primary hover:bg-s-primary-h text-white" data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'dashboard', icon: BarChart3, label: 'Dashboard & Analytics' },
    { id: 'packages', icon: Package, label: 'Packages' },
    { id: 'fleet', icon: Car, label: 'Fleet' },
    { id: 'team', icon: Users, label: 'Team' },
    { id: 'content', icon: Settings, label: 'Site Content' },
    { id: 'legal', icon: FileText, label: 'Legal Pages' },
    { id: 'media', icon: Image, label: 'Media' },
    { id: 'enquiries', icon: Mail, label: 'Enquiries' },
  ];

  return (
    <div className="min-h-screen bg-s-bg flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-s-bg2 border-r border-s-border transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-s-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_12ccf344-159c-4ed2-aa19-979c94946606/artifacts/vioo559c_Sentora%20Logo_Primary%20Symbol.png" alt="Sentora" className="w-full h-full object-contain" />
            </div>
            {sidebarOpen && <span className="font-bold text-s-text">Sentora Holidays</span>}
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              data-testid={`sidebar-${item.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                activeTab === item.id ? 'bg-s-primary text-white' : 'text-s-text2 hover:bg-s-card hover:text-s-text'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-s-border">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-s-text2 hover:bg-s-primary/20 hover:text-s-primary transition-all" data-testid="logout-btn">
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-s-bg2 border-b border-s-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-s-text2 hover:text-s-text" data-testid="toggle-sidebar-btn"><Menu className="w-6 h-6" /></button>
            <h1 className="text-xl font-semibold text-s-text capitalize">{activeTab}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-s-text2 text-sm">{user?.name}</span>
            <div className="w-8 h-8 bg-s-primary rounded-full flex items-center justify-center text-white font-bold">{user?.name?.charAt(0)}</div>
          </div>
        </header>

        <div className="p-6">
          {activeTab === 'dashboard' && <DashboardTab dashboardStats={dashboardStats} token={token} />}
          {activeTab === 'packages' && <PackagesTab packages={packages} onCreateModal={openCreateModal} onEditModal={openEditModal} onDelete={handleDelete} />}
          {activeTab === 'fleet' && <FleetTab fleet={fleet} onCreateModal={openCreateModal} onEditModal={openEditModal} onDelete={handleDelete} />}
          {activeTab === 'team' && <TeamTab teamMembers={teamMembers} onCreateModal={openCreateModal} onEditModal={openEditModal} onDelete={handleDelete} />}
          {activeTab === 'enquiries' && <EnquiriesTab enquiries={enquiries} onStatusChange={handleEnquiryStatus} onDelete={handleDelete} />}
          {activeTab === 'content' && <SiteContentTab siteContent={siteContent} setSiteContent={setSiteContent} saveSiteContent={saveSiteContent} savingContent={savingContent} token={token} />}
          {activeTab === 'legal' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-s-text">Legal Pages</h2>
                <Button onClick={saveLegalContent} disabled={savingLegal} className="bg-s-primary hover:bg-s-primary-h text-white">
                  {savingLegal ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : 'Save All'}
                </Button>
              </div>
              
              {/* Privacy Policy */}
              <div className="bg-s-card rounded-xl border border-s-border p-6">
                <h3 className="font-semibold text-s-text mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-s-primary" /> Privacy Policy
                </h3>
                <p className="text-xs text-s-muted mb-3">Use # for headings, ## for subheadings, - for bullet points. Separate paragraphs with blank lines.</p>
                <textarea 
                  value={legalContent.privacy_policy || ''} 
                  onChange={(e) => setLegalContent({...legalContent, privacy_policy: e.target.value})}
                  className="w-full h-64 bg-s-bg border border-s-border rounded-lg p-4 text-s-text text-sm font-mono resize-y"
                  placeholder="# Privacy Policy&#10;&#10;Welcome to Sentora Holidays...&#10;&#10;## Information We Collect&#10;&#10;- Personal information&#10;- Travel preferences"
                />
                <a href="/privacy-policy" target="_blank" className="text-xs text-s-primary hover:underline mt-2 inline-block">Preview Page →</a>
              </div>
              
              {/* Terms & Conditions */}
              <div className="bg-s-card rounded-xl border border-s-border p-6">
                <h3 className="font-semibold text-s-text mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-s-primary" /> Terms & Conditions
                </h3>
                <p className="text-xs text-s-muted mb-3">Define booking terms, traveler responsibilities, liability clauses, etc.</p>
                <textarea 
                  value={legalContent.terms_conditions || ''} 
                  onChange={(e) => setLegalContent({...legalContent, terms_conditions: e.target.value})}
                  className="w-full h-64 bg-s-bg border border-s-border rounded-lg p-4 text-s-text text-sm font-mono resize-y"
                  placeholder="# Terms & Conditions&#10;&#10;By using Sentora Holidays services...&#10;&#10;## Booking Terms&#10;&#10;- All bookings are subject to availability"
                />
                <a href="/terms-conditions" target="_blank" className="text-xs text-s-primary hover:underline mt-2 inline-block">Preview Page →</a>
              </div>
              
              {/* Refund Policy */}
              <div className="bg-s-card rounded-xl border border-s-border p-6">
                <h3 className="font-semibold text-s-text mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-s-primary" /> Refund & Cancellation Policy
                </h3>
                <p className="text-xs text-s-muted mb-3">Define cancellation windows, refund percentages, and non-refundable items.</p>
                <textarea 
                  value={legalContent.refund_policy || ''} 
                  onChange={(e) => setLegalContent({...legalContent, refund_policy: e.target.value})}
                  className="w-full h-64 bg-s-bg border border-s-border rounded-lg p-4 text-s-text text-sm font-mono resize-y"
                  placeholder="# Refund & Cancellation Policy&#10;&#10;We understand plans can change...&#10;&#10;## Cancellation by Traveler&#10;&#10;- More than 30 days: Full refund"
                />
                <a href="/refund-policy" target="_blank" className="text-xs text-s-primary hover:underline mt-2 inline-block">Preview Page →</a>
              </div>
            </div>
          )}
          {activeTab === 'media' && <MediaManager token={token} />}
        </div>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-s-bg2 border-s-border text-s-text max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editItem ? 'Edit' : 'Create'} {modalType}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {modalType === 'package' && (
              <div className="space-y-6">
                {/* Basic Info Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">Basic Information</h3>
                  <div className="space-y-4">
                    <div><Label>Package Title *</Label><Input value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Kerala Backwaters Paradise" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Destination *</Label><Input value={formData.destination || ''} onChange={(e) => setFormData({...formData, destination: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Kerala" /></div>
                      <div><Label>State</Label><Input value={formData.state || ''} onChange={(e) => setFormData({...formData, state: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Kerala" /></div>
                    </div>
                    
                    {/* Featured Toggle */}
                    <div className="flex items-center gap-3 p-3 bg-s-primary/10 border border-s-primary/30 rounded-lg">
                      <input 
                        type="checkbox" 
                        id="is_featured"
                        checked={formData.is_featured || false}
                        onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                        className="w-5 h-5 rounded border-s-primary text-s-primary focus:ring-s-primary"
                      />
                      <label htmlFor="is_featured" className="cursor-pointer">
                        <span className="text-sm font-medium text-s-primary">★ Featured Destination</span>
                        <p className="text-xs text-s-muted">Show in homepage search popup as traveler favorite</p>
                      </label>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="mb-2 block">Regions (select multiple)</Label>
                        <div className="space-y-2 bg-s-card border border-s-border rounded-lg p-3">
                          {[
                            { value: 'south', label: 'South India' },
                            { value: 'north', label: 'North India' },
                            { value: 'east', label: 'East India' },
                            { value: 'west', label: 'West India' }
                          ].map(opt => {
                            const regions = Array.isArray(formData.region) ? formData.region : [formData.region || 'south'];
                            const isChecked = regions.includes(opt.value);
                            return (
                              <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-s-text2 hover:text-s-text">
                                <input 
                                  type="checkbox" 
                                  checked={isChecked}
                                  onChange={(e) => {
                                    let newRegions = [...regions];
                                    if (e.target.checked) {
                                      if (!newRegions.includes(opt.value)) newRegions.push(opt.value);
                                    } else {
                                      newRegions = newRegions.filter(r => r !== opt.value);
                                      if (newRegions.length === 0) newRegions = ['south']; // At least one required
                                    }
                                    setFormData({...formData, region: newRegions});
                                  }}
                                  className="w-4 h-4 rounded border-s-border text-s-primary focus:ring-s-primary"
                                />
                                {opt.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <Label className="mb-2 block">Themes (select multiple)</Label>
                        <div className="space-y-2 bg-s-card border border-s-border rounded-lg p-3 max-h-48 overflow-y-auto">
                          {[
                            { value: 'beach', label: 'Beach' },
                            { value: 'spiritual', label: 'Spiritual' },
                            { value: 'cultural', label: 'Cultural Heritage' },
                            { value: 'hill_stations', label: 'Hill Stations' },
                            { value: 'ayurveda', label: 'Ayurveda & Wellness' },
                            { value: 'wildlife', label: 'Wildlife & Safari' },
                            { value: 'adventure', label: 'Adventure' },
                            { value: 'nature', label: 'Nature & Scenic' },
                            { value: 'honeymoon', label: 'Honeymoon' }
                          ].map(opt => {
                            const themes = Array.isArray(formData.theme) ? formData.theme : [formData.theme || 'cultural'];
                            const isChecked = themes.includes(opt.value);
                            return (
                              <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-s-text2 hover:text-s-text">
                                <input 
                                  type="checkbox" 
                                  checked={isChecked}
                                  onChange={(e) => {
                                    let newThemes = [...themes];
                                    if (e.target.checked) {
                                      if (!newThemes.includes(opt.value)) newThemes.push(opt.value);
                                    } else {
                                      newThemes = newThemes.filter(t => t !== opt.value);
                                      if (newThemes.length === 0) newThemes = ['cultural']; // At least one required
                                    }
                                    setFormData({...formData, theme: newThemes});
                                  }}
                                  className="w-4 h-4 rounded border-s-border text-s-primary focus:ring-s-primary"
                                />
                                {opt.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nights</Label>
                        <div className="bg-s-bg2 border border-s-border rounded-md px-3 py-2 text-s-text">
                          {Math.max(0, (formData.itinerary?.length || 1) - 1)}
                          <span className="text-s-muted text-xs ml-2">(auto from itinerary)</span>
                        </div>
                      </div>
                      <div>
                        <Label>Days</Label>
                        <div className="bg-s-bg2 border border-s-border rounded-md px-3 py-2 text-s-text">
                          {formData.itinerary?.length || 1}
                          <span className="text-s-muted text-xs ml-2">(auto from itinerary)</span>
                        </div>
                      </div>
                    </div>
                    <div><Label>Cities Covered (comma separated)</Label><Input value={formData.citiesInput !== undefined ? formData.citiesInput : (Array.isArray(formData.cities) ? formData.cities.join(', ') : (formData.cities || ''))} onChange={(e) => setFormData({...formData, citiesInput: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Kochi, Munnar, Alleppey" /></div>
                  </div>
                </div>

                {/* Images Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">Images</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1"><Label>Main Image URL *</Label><ImagePickerButton value={formData.image || ''} onChange={(url) => setFormData({...formData, image: url})} token={token} /></div>
                      <Input value={formData.image || ''} onChange={(e) => setFormData({...formData, image: e.target.value})} className="bg-s-card border-s-border" />
                    </div>
                    
                    {/* Gallery Images with Bulk Upload */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Gallery Images</Label>
                        <div className="flex gap-2">
                          <input 
                            type="file" 
                            id="gallery-bulk-upload" 
                            className="hidden" 
                            multiple 
                            accept="image/*" 
                            onChange={async (e) => {
                              const files = Array.from(e.target.files || []);
                              if (!files.length) return;
                              const uploadedUrls = [];
                              for (const file of files) {
                                const fd = new FormData();
                                fd.append('file', file);
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/media/upload`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${token}` },
                                    body: fd
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    uploadedUrls.push(`${API_URL}${data.url}`);
                                  }
                                } catch (err) {
                                  console.error('Upload failed:', file.name, err);
                                }
                              }
                              if (uploadedUrls.length > 0) {
                                const existing = Array.isArray(formData.images) ? formData.images : [];
                                setFormData({...formData, images: [...existing, ...uploadedUrls]});
                              }
                              e.target.value = '';
                            }}
                          />
                          <label htmlFor="gallery-bulk-upload" className="cursor-pointer px-3 py-1.5 text-xs bg-s-primary text-white rounded-lg hover:bg-s-primary-h flex items-center gap-1">
                            <Upload size={14} /> Bulk Upload
                          </label>
                        </div>
                      </div>
                      
                      {/* Gallery Preview with Numbers */}
                      {(formData.images?.length > 0) && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {formData.images.map((url, idx) => (
                            <div key={idx} className="relative group">
                              <div className="aspect-square bg-s-bg2 rounded-lg overflow-hidden border border-s-border">
                                <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                              </div>
                              <div className="absolute top-1 left-1 bg-s-primary text-white text-xs px-1.5 py-0.5 rounded font-bold">
                                {idx + 1}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newImages = formData.images.filter((_, i) => i !== idx);
                                  setFormData({...formData, images: newImages});
                                }}
                                className="absolute top-1 right-1 bg-s-primary text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={12} />
                              </button>
                              <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {idx > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newImages = [...formData.images];
                                      [newImages[idx], newImages[idx - 1]] = [newImages[idx - 1], newImages[idx]];
                                      setFormData({...formData, images: newImages});
                                    }}
                                    className="bg-s-bg/80 text-s-text text-xs px-1.5 py-0.5 rounded"
                                  >
                                    ←
                                  </button>
                                )}
                                {idx < formData.images.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newImages = [...formData.images];
                                      [newImages[idx], newImages[idx + 1]] = [newImages[idx + 1], newImages[idx]];
                                      setFormData({...formData, images: newImages});
                                    }}
                                    className="bg-s-bg/80 text-s-text text-xs px-1.5 py-0.5 rounded"
                                  >
                                    →
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <Textarea 
                        value={Array.isArray(formData.images) ? formData.images.join('\n') : ''} 
                        onChange={(e) => setFormData({...formData, images: e.target.value.split('\n').map(s => s.trim()).filter(s => s)})} 
                        className="bg-s-card border-s-border text-xs" 
                        rows={2} 
                        placeholder="Or paste URLs (one per line)..."
                      />
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">Description</h3>
                  <div className="space-y-4">
                    <div><Label>Brief Overview</Label><Textarea value={formData.brief || ''} onChange={(e) => setFormData({...formData, brief: e.target.value})} className="bg-s-card border-s-border" rows={3} placeholder="A short description of this package..." /></div>
                    <div><Label>Highlights (comma separated)</Label><Textarea value={formData.highlightsInput !== undefined ? formData.highlightsInput : (Array.isArray(formData.highlights) ? formData.highlights.join(', ') : '')} onChange={(e) => setFormData({...formData, highlightsInput: e.target.value})} className="bg-s-card border-s-border" rows={2} placeholder="Houseboat stay, Tea plantations, Kathakali show" /></div>
                    <div><Label>Best Time to Visit</Label><Input value={formData.best_time_to_visit || ''} onChange={(e) => setFormData({...formData, best_time_to_visit: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., September to March" /></div>
                    <div><Label>Interesting Facts / About the Place</Label><Textarea value={formData.interesting_facts || ''} onChange={(e) => setFormData({...formData, interesting_facts: e.target.value})} className="bg-s-card border-s-border" rows={3} placeholder="Tell visitors about interesting things about this destination..." /></div>
                  </div>
                </div>

                {/* Day-wise Itinerary Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">Day-wise Itinerary</h3>
                  <p className="text-xs text-s-muted mb-3">Add details for each day. Select at least one time slot (Morning/Afternoon/Evening) per day.</p>
                  <div className="space-y-4">
                    {(formData.itinerary || []).map((item, idx) => (
                      <div key={idx} className="bg-s-card p-4 rounded-lg border border-s-border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-s-primary">Day {item.day || idx + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => {
                            const newItinerary = [...(formData.itinerary || [])];
                            newItinerary.splice(idx, 1);
                            setFormData({...formData, itinerary: newItinerary});
                          }} className="text-s-primary hover:text-s-primary h-6 px-2">Remove</Button>
                        </div>
                        <Input value={item.title || ''} onChange={(e) => {
                          const newItinerary = [...(formData.itinerary || [])];
                          newItinerary[idx] = {...newItinerary[idx], title: e.target.value};
                          setFormData({...formData, itinerary: newItinerary});
                        }} className="bg-s-bg border-s-border mb-2" placeholder="Day Title (e.g., Arrival in Kochi, Explore Munnar)" />
                        
                        <Input value={item.highlight || ''} onChange={(e) => {
                          const newItinerary = [...(formData.itinerary || [])];
                          newItinerary[idx] = {...newItinerary[idx], highlight: e.target.value};
                          setFormData({...formData, itinerary: newItinerary});
                        }} className="bg-s-bg border-s-border mb-3 text-sm" placeholder="Day Highlight (e.g., Tea Plantations, Houseboat Experience)" />
                        
                        {/* Photo */}
                        <div className="flex items-center gap-2 mb-3">
                          <Input value={item.image || ''} onChange={(e) => {
                            const newItinerary = [...(formData.itinerary || [])];
                            newItinerary[idx] = {...newItinerary[idx], image: e.target.value};
                            setFormData({...formData, itinerary: newItinerary});
                          }} className="bg-s-bg border-s-border flex-1" placeholder="Day photo URL" />
                          <ImagePickerButton value={item.image || ''} onChange={(url) => {
                            const newItinerary = [...(formData.itinerary || [])];
                            newItinerary[idx] = {...newItinerary[idx], image: url};
                            setFormData({...formData, itinerary: newItinerary});
                          }} token={token} />
                        </div>
                        {item.image && <img src={item.image} alt={`Day ${idx+1}`} className="mb-3 h-16 w-20 object-cover rounded border border-s-border" />}
                        
                        {/* Activities - Single merged field */}
                        <div>
                          <Label className="text-xs text-s-muted mb-1 block">Day Activities</Label>
                          <Textarea value={item.activities || ''} onChange={(e) => {
                            const newItinerary = [...(formData.itinerary || [])];
                            newItinerary[idx] = {...newItinerary[idx], activities: e.target.value};
                            setFormData({...formData, itinerary: newItinerary});
                          }} className="bg-s-bg border-s-border" rows={4} placeholder="Describe all activities for this day..." />
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => {
                      const newItinerary = [...(formData.itinerary || [])];
                      newItinerary.push({ day: newItinerary.length + 1, title: '', highlight: '', image: '', activities: '' });
                      setFormData({...formData, itinerary: newItinerary});
                    }} className="w-full border-dashed border-s-border text-s-text2">+ Add Day</Button>
                  </div>
                </div>

                {/* Key Highlights Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">Key Highlights</h3>
                  <div className="space-y-4">
                    <div><Label>Key Highlights (comma separated)</Label><Textarea value={formData.inclusionsInput !== undefined ? formData.inclusionsInput : (Array.isArray(formData.inclusions) ? formData.inclusions.join(', ') : '')} onChange={(e) => setFormData({...formData, inclusionsInput: e.target.value})} className="bg-s-card border-s-border" rows={3} placeholder="Accommodation, Breakfast, Sightseeing, Transfers, Guide Services, Local Experiences" /></div>
                  </div>
                </div>

                {/* FAQs Section */}
                <div className="border-b border-s-border pb-4">
                  <h3 className="text-sm font-semibold text-s-text mb-3">FAQs</h3>
                  <div className="space-y-3">
                    {(formData.faqs || []).map((faq, idx) => (
                      <div key={idx} className="bg-s-card p-3 rounded-lg border border-s-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-s-primary">FAQ {idx + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => {
                            const newFaqs = [...(formData.faqs || [])];
                            newFaqs.splice(idx, 1);
                            setFormData({...formData, faqs: newFaqs});
                          }} className="text-s-primary hover:text-s-primary h-6 px-2">Remove</Button>
                        </div>
                        <Input value={faq.question || ''} onChange={(e) => {
                          const newFaqs = [...(formData.faqs || [])];
                          newFaqs[idx] = {...newFaqs[idx], question: e.target.value};
                          setFormData({...formData, faqs: newFaqs});
                        }} className="bg-s-bg border-s-border mb-2" placeholder="Question" />
                        <Textarea value={faq.answer || ''} onChange={(e) => {
                          const newFaqs = [...(formData.faqs || [])];
                          newFaqs[idx] = {...newFaqs[idx], answer: e.target.value};
                          setFormData({...formData, faqs: newFaqs});
                        }} className="bg-s-bg border-s-border" rows={2} placeholder="Answer" />
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => {
                      const newFaqs = [...(formData.faqs || [])];
                      newFaqs.push({ question: '', answer: '' });
                      setFormData({...formData, faqs: newFaqs});
                    }} className="w-full border-dashed border-s-border text-s-text2">+ Add FAQ</Button>
                  </div>
                </div>

                {/* Additional Options Section */}
                <div>
                  <h3 className="text-sm font-semibold text-s-text mb-3">Additional Options</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.guide_available ?? true} onChange={(e) => setFormData({...formData, guide_available: e.target.checked})} className="rounded" />
                        <Label>Guide Available</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={formData.is_active ?? true} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="rounded" />
                        <Label>Active (Visible on site)</Label>
                      </div>
                    </div>
                    <div><Label>Languages Available (comma separated)</Label><Input value={Array.isArray(formData.languages) ? formData.languages.join(', ') : 'English'} onChange={(e) => setFormData({...formData, languages: e.target.value.split(',').map(s => s.trim()).filter(s => s)})} className="bg-s-card border-s-border" placeholder="English, Hindi, Tamil" /></div>
                  </div>
                </div>
              </div>
            )}
            {modalType === 'fleet' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Vehicle Name</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-s-card border-s-border" /></div>
                  <div><Label>Type</Label><Input value={formData.type || ''} onChange={(e) => setFormData({...formData, type: e.target.value})} placeholder="SUV, Sedan, Bus..." className="bg-s-card border-s-border" /></div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1"><Label>Main Image URL</Label><ImagePickerButton value={formData.image || ''} onChange={(url) => setFormData({...formData, image: url})} token={token} /></div>
                  <Input value={formData.image || ''} onChange={(e) => setFormData({...formData, image: e.target.value})} className="bg-s-card border-s-border" />
                </div>
                
                {/* Gallery Images for Fleet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Gallery Images</Label>
                    <div className="flex gap-2">
                      <input 
                        type="file" 
                        id="fleet-gallery-upload" 
                        className="hidden" 
                        multiple 
                        accept="image/*" 
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          if (!files.length) return;
                          const uploadedUrls = [];
                          for (const file of files) {
                            const fd = new FormData();
                            fd.append('file', file);
                            try {
                              const res = await fetch(`${API_URL}/api/admin/media/upload`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                                body: fd
                              });
                              if (res.ok) {
                                const data = await res.json();
                                uploadedUrls.push(`${API_URL}${data.url}`);
                              }
                            } catch (err) {
                              console.error('Upload failed:', file.name, err);
                            }
                          }
                          if (uploadedUrls.length > 0) {
                            const existing = Array.isArray(formData.images) ? formData.images : [];
                            setFormData({...formData, images: [...existing, ...uploadedUrls]});
                          }
                          e.target.value = '';
                        }}
                      />
                      <label htmlFor="fleet-gallery-upload" className="cursor-pointer px-3 py-1.5 text-xs bg-s-primary text-white rounded-lg hover:bg-s-primary-h flex items-center gap-1">
                        <Upload size={14} /> Bulk Upload
                      </label>
                    </div>
                  </div>
                  
                  {/* Gallery Preview */}
                  {(formData.images?.length > 0) && (
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {formData.images.map((url, idx) => (
                        <div key={idx} className="relative group">
                          <div className="aspect-video bg-s-bg2 rounded overflow-hidden border border-s-border">
                            <img src={url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                          <div className="absolute top-1 left-1 bg-s-primary text-white text-xs px-1 py-0.5 rounded font-bold">
                            {idx + 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.images.filter((_, i) => i !== idx);
                              setFormData({...formData, images: newImages});
                            }}
                            className="absolute top-1 right-1 bg-s-primary text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <Textarea 
                    value={Array.isArray(formData.images) ? formData.images.join('\n') : ''} 
                    onChange={(e) => setFormData({...formData, images: e.target.value.split('\n').map(s => s.trim()).filter(s => s)})} 
                    className="bg-s-card border-s-border text-xs" 
                    rows={2} 
                    placeholder="Or paste URLs (one per line)..."
                  />
                </div>
                
                <div><Label>Capacity</Label><Input value={formData.capacity || ''} onChange={(e) => setFormData({...formData, capacity: e.target.value})} placeholder="4+1 Seater" className="bg-s-card border-s-border" /></div>
                <div><Label>Features (comma separated)</Label><Textarea value={Array.isArray(formData.features) ? formData.features.join(', ') : ''} onChange={(e) => setFormData({...formData, features: e.target.value.split(',').map(s => s.trim()).filter(s => s)})} className="bg-s-card border-s-border" /></div>
              </>
            )}
            {modalType === 'team' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Rajesh Menon" /></div>
                  <div><Label>Role / Designation *</Label><Input value={formData.role || ''} onChange={(e) => setFormData({...formData, role: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., Founder & Chairman" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Years of Experience</Label><Input type="number" value={formData.years_of_experience || 0} onChange={(e) => setFormData({...formData, years_of_experience: parseInt(e.target.value) || 0})} className="bg-s-card border-s-border" placeholder="e.g., 25" /></div>
                  <div><Label>Specialization</Label><Input value={formData.specialization || ''} onChange={(e) => setFormData({...formData, specialization: e.target.value})} className="bg-s-card border-s-border" placeholder="e.g., South India Tours" /></div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1"><Label>Photo URL</Label><ImagePickerButton value={formData.image || ''} onChange={(url) => setFormData({...formData, image: url})} token={token} /></div>
                  <Input value={formData.image || ''} onChange={(e) => setFormData({...formData, image: e.target.value})} className="bg-s-card border-s-border" placeholder="https://..." />
                </div>
                {formData.image && <div className="flex justify-center"><img src={formData.image} alt="Preview" className="w-24 h-32 object-cover rounded-lg border border-s-border" /></div>}
                <div><Label>Short Bio</Label><Textarea value={formData.bio || ''} onChange={(e) => setFormData({...formData, bio: e.target.value})} className="bg-s-card border-s-border" rows={3} placeholder="Brief description of experience and expertise..." /></div>
                <div><Label>Display Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} className="bg-s-card border-s-border w-24" /><p className="text-s-muted text-xs mt-1">Lower number appears first (0 = first)</p></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-s-border text-s-text2">Cancel</Button>
            <Button onClick={editItem ? handleUpdate : handleCreate} disabled={loading} className="bg-s-primary hover:bg-s-primary-h text-white" data-testid="modal-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
